import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Heart, Sparkles } from 'lucide-react';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import NotFoundPage from './NotFoundPage';

import HeroSection from '../components/landing/HeroSection';
import ProblemSection from '../components/landing/ProblemSection';
import SolutionSection from '../components/landing/SolutionSection';
import BenefitsSection from '../components/landing/BenefitsSection';
import FeaturesSection from '../components/landing/FeaturesSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import PricingSection from '../components/landing/PricingSection';
import DownloadSection from '../components/landing/DownloadSection';

import { getProgram } from '../data/programs';
import { usePublicPlans } from '../hooks/usePublicPlans';
import { safeJsonLd } from '../utils/seo';

const ProgramLandingPage = () => {
  const { programSlug } = useParams();
  const navigate = useNavigate();
  const program = getProgram(programSlug);
  const { plans } = usePublicPlans();

  // Per-program theme tokens — drive every section via CSS custom properties.
  const themeStyle = useMemo(() => {
    if (!program) return {};
    return {
      '--ls-primary':   program.colors.primary,
      '--ls-secondary': program.colors.secondary,
      '--ls-accent':    program.colors.accent,
      '--ls-glow':      program.colors.glow,
      background: 'var(--bg-primary)',
      minHeight: '100vh',
    };
  }, [program]);

  useEffect(() => {
    // Scroll to top on slug change so navigation between programs feels clean.
    window.scrollTo({ top: 0 });
  }, [programSlug]);

  if (!program) {
    return <NotFoundPage />;
  }

  const openCta = (url) => {
    if (!url) return;
    if (url.startsWith('#')) {
      document.querySelector(url)?.scrollIntoView({ behavior: 'smooth' });
    } else if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  return (
    <>
      <Helmet>
        <title>{program.seo.title}</title>
        <meta name="description" content={program.seo.description} />
        {program.seo.keywords?.length > 0 && (
          <meta name="keywords" content={program.seo.keywords.join(', ')} />
        )}
        <link rel="canonical" href={`https://lifyup.com.br/${program.slug}`} />
        <meta property="og:title" content={program.seo.title} />
        <meta property="og:description" content={program.seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://lifyup.com.br/${program.slug}`} />
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: `LifeUp ${program.name}`,
          description: program.seo.description,
          applicationCategory: 'HealthApplication',
          operatingSystem: 'iOS, Android, Web',
          url: `https://lifyup.com.br/${program.slug}`,
          image: program.heroImage,
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'BRL',
          },
          publisher: {
            '@type': 'Organization',
            name: 'LifeUp',
            url: 'https://lifyup.com.br',
          },
        })}</script>
      </Helmet>

      <div className="lp-theme" style={themeStyle}>
        <Header />
        <main>
          <HeroSection
            eyebrow={`Programa ${program.name}`}
            title={program.subtitle || program.tagline}
            subtitle={program.description}
            badges={[
              { icon: <Shield size={16} />,   label: 'Privacidade absoluta' },
              { icon: <Heart size={16} />,    label: 'Comunidade 24/7' },
              { icon: <Sparkles size={16} />, label: 'Baseado em ciência' },
            ]}
            primaryCta={{ label: program.cta.label, onClick: () => openCta(program.cta.url) }}
            secondaryCta={
              program.cta.secondaryLabel
                ? { label: program.cta.secondaryLabel, onClick: () => openCta(program.cta.secondaryUrl) }
                : undefined
            }
            image={program.heroImage}
            trust={
              program.stats?.length
                ? {
                    avatars: [1, 2, 3, 4].map((i) => `/assets/images/avatar-${i}.jpg`),
                    label: `${program.stats[0].value} ${program.stats[0].label.toLowerCase()}`,
                  }
                : undefined
            }
          />

          <ProblemSection
            eyebrow="O problema"
            title="Você reconhece algum desses padrões?"
            subtitle={`Esses são os ciclos que o programa ${program.name} foi feito para quebrar.`}
            items={program.problems}
          />

          <SolutionSection
            eyebrow="A solução"
            title={program.solution.headline}
            description={program.solution.description}
            bullets={program.solution.bullets}
            image={program.heroImage}
          />

          <BenefitsSection
            eyebrow="O que você ganha"
            title={`O que muda na sua vida com o ${program.name}`}
            subtitle="Resultados reais relatados pela maioria dos nossos usuários."
            items={program.benefits}
          />

          <FeaturesSection
            eyebrow="Recursos do programa"
            title={`Tudo que o ${program.name} oferece`}
            subtitle="Ferramentas pensadas especificamente para a jornada deste programa."
            items={program.features}
          />

          <TestimonialsSection
            eyebrow="Histórias reais"
            title={`Quem já trilhou esta jornada`}
            subtitle="Pessoas que saíram do ciclo usando o programa."
            items={program.testimonials}
          />

          <PricingSection
            eyebrow="Planos"
            title={program.pricing.headline}
            description={program.pricing.description}
            plans={plans}
            onSelect={() => navigate('/assinar')}
          />

          <DownloadSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ProgramLandingPage;
